import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { InputFieldComponent } from '../../../form';
import { DEFAULT_FG } from '../../constants/default-form';
import { DynamicFormDTO, ISection } from '../../models';
import { FormMode } from '../../models/form-mode.enum';
import { FormBuilderService } from '../../services/builder.service';
import { PreviewContainerComponent } from '../preview-container';
import { FormSectionBuilderComponent } from '../section-builder';

@Component({
  selector: 'siat-form-builder',
  templateUrl: './dynamic-form-builder.component.html',
  styleUrls: ['./dynamic-form-builder.component.scss'],
  providers: [FormBuilderService],
  imports: [
    MatCardModule,
    MatDialogModule,
    MatTooltip,
    PreviewContainerComponent,
    NgClass,
    MatIconModule,
    DragDropModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    FormSectionBuilderComponent,
    InputFieldComponent,
  ],
})
export class DynamicFormBuilderComponent {
  /**
   * The service that builds the form.
   */
  readonly dynamicFbService = inject(FormBuilderService);

  /**
   * The index of the selected section.
   */
  selectedSectionIndex: number | null = 0;

  /**
   * The set of sections that can be dragged.
   */
  dragFieldsEnabledSections = new Set<number>();

  /**
   * A flag that indicates if the preview should be shown.
   */
  showPreview = true;

  /**
   * The dynamic form.
   */
  dynamicForm = input<DynamicFormDTO>();

  /**
   * An event that is emitted when the save button is clicked.
   */
  @Output()
  onClickSave = new EventEmitter<DynamicFormDTO>();

  constructor() {
    effect(() => {
      const dto = this.dynamicForm();
      const mode = dto ? FormMode.Editing : FormMode.Building;
      const data = dto ? JSON.parse(dto.dynamicJson) : DEFAULT_FG;
      const fg = this.dynamicFbService.buildForm(data, mode);

      this.dynamicFbService.setFormGroup(fg);
      this.dynamicFbService.setDynamicFormDTO(dto);
    });
  }

  /**
   * It returns the base form group.
   * @returns {FormGroup}
   */
  get formGroup(): FormGroup {
    return this.dynamicFbService.formGroup()!;
  }

  /**
   * It returns the sections form array.
   * @returns {FormArray}
   */
  get sections(): FormArray {
    return this.formGroup.get('sections') as FormArray;
  }

  /**
   * On add section button click.
   */
  addSection(): void {
    const sectionArray = this.dynamicFbService.getSectionFormArray();
    if (!sectionArray) return;
    const newId = this.dynamicFbService.generateId(
      'section',
      sectionArray.length
    );

    const sectionFormGroup = this.dynamicFbService.buildSection(
      {
        id: newId,
        title: '',
        description: '',
      },
      FormMode.Editing
    );

    sectionArray.push(sectionFormGroup);

    this.selectSection(sectionArray.length - 1);
  }

  /**
   * On delete section button click.
   */
  deleteSection(id: string): void {
    if (this.sections.length <= 1) return;

    const index = this.sections.controls.findIndex(s => s.value.id === id);

    if (index !== -1) {
      this.sections.removeAt(index);
      this.selectedSectionIndex = this.sections.length > 0 ? 0 : null;
    }
  }

  /**
   * On section dropped event.
   * @param {CdkDragDrop<ISection[]>}
   */
  onSectionDropped(event: CdkDragDrop<ISection[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      const sectionArray = this.sections;
      const movedSection = sectionArray.at(event.previousIndex);

      sectionArray.removeAt(event.previousIndex);
      sectionArray.insert(event.currentIndex, movedSection);

      this.selectSection(event.currentIndex);
    }
  }

  /**
   * It returns the dynamic form dto.
   * @returns {DynamicFormDTO}
   */
  get dynamicFormDTO(): DynamicFormDTO {
    const rawValue = this.formGroup.getRawValue();
    return {
      id: this.dynamicForm()?.id,
      title: rawValue.title,
      description: rawValue.description,
      dynamicJson: JSON.stringify(rawValue),
    } as DynamicFormDTO;
  }

  /**
   * It sets the selected section index.
   * @param {number} index
   */
  selectSection(index: number): void {
    this.selectedSectionIndex = index;
  }

  /**
   * It updates the drag fields enabled sections.
   * @param {number} index
   * @param {boolean} enabled
   */
  updateDragFieldsEnabled(index: number, enabled: boolean) {
    if (enabled) {
      this.dragFieldsEnabledSections.add(index);
    } else {
      this.dragFieldsEnabledSections.delete(index);
    }
    this.selectedSectionIndex = index;
  }

  /**
   * It returns true if any drag fields are enabled.
   * @returns {boolean}
   */
  isAnyDragFieldsEnabled(): boolean {
    return this.dragFieldsEnabledSections.size > 0;
  }

  /**
   * Helper function for ngFor.
   */
  trackByFn = (index: number) => index;

  /**
   * It toggles the preview.
   */
  showPreviewClicked(): void {
    this.showPreview = !this.showPreview;
  }

  /**
   * It returns the form group.
   * @param {AbstractControl} control
   */
  asFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  /**
   * It returns true if the section drag is disabled.
   * @returns {boolean}
   */
  get isSectionDragDisabled(): boolean {
    return this.sections.length <= 1 || this.isAnyDragFieldsEnabled();
  }

  /**
   * On click save event.
   */
  loadOnClickSave(): void {
    this.onClickSave.emit(this.dynamicFormDTO);
  }
}
